import clsx from 'clsx'
import { Zap } from 'lucide-react'
import { CATEGORY_COLORS } from './nudgeCategoryStyles'
import type { LibraryNudge } from './libraryNudge'
import { NudgeModalResearchSection } from './NudgeModalResearchSection'

type PendingActions = {
  onApprove: () => void
  onReject: () => void
  busy?: boolean
}

type Props = {
  nudge: LibraryNudge
  onClose: () => void
  /** Admin: raw row fields for research section */
  sourceRefs?: unknown
  pendingReviewActions?: PendingActions
  /** Wider modal when showing research + actions */
  wide?: boolean
}

export function NudgePreviewModal({
  nudge,
  onClose,
  sourceRefs,
  pendingReviewActions,
  wide,
}: Props) {
  return (
    <div
      className="fixed inset-0 bg-ink/20 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className={clsx(
          'bg-warm-card border border-warm-border rounded-2xl p-6 w-full shadow-card max-h-[90vh] overflow-y-auto',
          wide ? 'max-w-2xl' : 'max-w-lg'
        )}
      >
        <div className="flex items-start justify-between mb-4">
          <span
            className={clsx(
              'text-xs font-medium px-2.5 py-1 rounded-full border capitalize',
              CATEGORY_COLORS[nudge.category] ?? 'bg-warm-inset text-ink-muted border-warm-border'
            )}
          >
            {nudge.category}
          </span>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink text-xl leading-none">
            &times;
          </button>
        </div>

        <h2 className="text-lg font-bold text-ink mb-3">{nudge.title}</h2>

        <div
          className="bg-warm-inset rounded-xl p-4 mb-4"
          style={{ border: '1px solid #e0d9cc', borderLeft: '4px solid #2A7A8A' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 bg-brand-teal rounded flex items-center justify-center">
              <Zap size={11} className="text-white" />
            </div>
            <span className="text-xs font-semibold text-ink">Duva</span>
            <span className="text-xs text-ink-soft">nudge bot</span>
          </div>
          <p className="text-sm text-ink-muted leading-relaxed whitespace-pre-wrap">{nudge.body}</p>
        </div>

        <div className="space-y-2 text-xs text-ink-soft">
          {nudge.science_source ? (
            <p>
              📚 <span className="text-ink-muted">{nudge.science_source}</span>
            </p>
          ) : null}
          <p>
            Channel: <span className="text-ink-muted capitalize">{nudge.channel}</span>
          </p>
          <p>
            Type:{' '}
            <span className="text-ink-muted capitalize">
              {nudge.system_target === 'system1' ? 'System 1 (fast, intuitive)' : 'System 2 (deliberate, reflective)'}
            </span>
          </p>
          <p>
            Effectiveness:{' '}
            <span className="text-brand-teal font-semibold">{Math.round(nudge.effectiveness_score * 100)}%</span>
          </p>
        </div>

        {sourceRefs !== undefined ? <NudgeModalResearchSection sourceRefs={sourceRefs} /> : null}

        {pendingReviewActions ? (
          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              disabled={pendingReviewActions.busy}
              onClick={() => pendingReviewActions.onApprove()}
              className="flex-1 text-white font-medium text-sm py-2.5 rounded-full transition-colors shadow-glow-teal disabled:opacity-50"
              style={{ background: '#2A7A8A' }}
            >
              Approve
            </button>
            <button
              type="button"
              disabled={pendingReviewActions.busy}
              onClick={() => pendingReviewActions.onReject()}
              className="flex-1 text-sm py-2.5 rounded-full border border-warm-border font-medium disabled:opacity-50"
            >
              Reject
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 text-sm py-2.5 rounded-full border border-warm-border text-ink-muted hover:bg-warm-inset"
            >
              Close
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onClose}
            className="mt-5 w-full text-white font-medium text-sm py-2.5 rounded-full transition-colors shadow-glow-teal"
            style={{ background: '#2A7A8A' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#236b7a')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#2A7A8A')}
          >
            Close
          </button>
        )}
      </div>
    </div>
  )
}
